import doctorWithAlgo from './doctor-with-algo.png'
import algoDoctorSeparate from './algo-doctor-separate.png'

const content = `<p>When designing the classes in an application, the natural approach is to look at the concepts of the domain and represent them with classes.</p>

<p>But sometimes those classes accumulate concerns and lose cohesion. To keep high cohesion, we break off the concerns of big classes into smaller classes.</p>

<p>It is important to recognize that sometimes this leads to classes that do not represent an object of the domain, and it’s ok. Those classes are called pure fabrications. Pure fabrication is one of the <a href="https://en.wikipedia.org/wiki/GRASP_(object-oriented_design)#:~:text=The%20different%20patterns%20and%20principles,to%20many%20software%20development%20projects.">GRASP</a> design principles.</p>

<p>Let’s illustrate with an example. Let’s consider a medical application where doctors have to verify their identity to be allowed to use the services.</p>

<p>There are several methods for a doctor to verify their identity. One uses an official identification protocol issued by the government. Another method is to take a picture of themselves and their ID and an external service checks that they match, then a request is sent to the KYC department to finalize the verification manually.</p>

<p>The first option is simpler but in some cases it fails, and the second option is longer but more reliable.</p>

<p>There is an algorithm to decide which option to present to a given user at a given time. This algorithm can be complicated, with dependencies on the database, the properties of the account, environment variables, and so on.</p>

<p>Where do we put this algorithm in the code?</p>

<p>Indeed, in a medical application, a class that represents a doctor is likely to handle many things already. If we put the algorithm in that class, we reduce its cohesion and couple it with other parts of the software:</p>

<img src=${doctorWithAlgo} alt="doctor-with-algo" />

<p>Mixing such concepts in the same class leads to large classes with <a href="high-cohesion">high coupling because of low cohesion</a>.</p>

<p>A more maintainable option is to create a class dedicated to choosing the identity verification method:</p>

<img src=${algoDoctorSeparate} alt="algo-doctor-separate" />

<p>This <code>IdentityVerificationAlgorithm</code> class is a Pure fabrication that doesn't map to an object in the domain. And it's ok. It helps maintain cohesion in the <code>Doctor</code> class and reduce coupling in the code.</p>

<p>There are many other examples of Pure fabrication in design patterns that we see in dedicated articles.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Some concerns don’t have a 1-to-1 mapping to domain objects</li><li>Pure fabrications offload other classes of such concerns to maintain their cohesion</li><li>It’s ok that Pure fabrications classes don’t map to domain objects</li></ul>
</div>`

export default content
